import isDeepEqual from 'fast-deep-equal';
import {
  LabelOrigin,
  LabelType,
  LabelValueDetails,
  LabelValueSource,
  TimeSignal
} from 'graphql/types';
import {
  NotesGroupByStrategy,
  UNCATEGORIZED_GROUP_KEY
} from './NotesGroupBySorter';

export class LabelAndTimeGroupByStrategy implements NotesGroupByStrategy {
  private readonly date: string;

  constructor(date: string) {
    this.date = date;
  }

  generateGroups(notes: TimeSignal[]): Record<string, TimeSignal[]> {
    return this.groupNotes(notes, this.date);
  }

  getGroupingLabels(note: TimeSignal): LabelValueDetails[] {
    const groupingLabels = note.labelValues.filter(
      (labelValue: LabelValueDetails) => {
        return (
          labelValue.label?.type !== LabelType.Comment &&
          labelValue.label?.origin === LabelOrigin.Timesheet
        );
      }
    );

    return groupingLabels;
  }

  private groupNotes(
    notes: TimeSignal[],
    date: string
  ): Record<string, TimeSignal[]> {
    const timeSignalGroups = new Map<string, TimeSignal[]>();

    this.handleUncategorizedNotes(notes, timeSignalGroups, date);
    this.handleCategorizedNotes(notes, timeSignalGroups, date);

    return Object.fromEntries(timeSignalGroups);
  }

  private handleUncategorizedNotes(
    notes: TimeSignal[],
    timeSignalGroups: Map<string, TimeSignal[]>,
    date: string
  ) {
    const uncategorizedNotes = notes.filter(
      this.isUncategorizedNote.bind(this)
    );
    if (uncategorizedNotes.length > 0) {
      timeSignalGroups.set(
        `${UNCATEGORIZED_GROUP_KEY}-${date}`,
        uncategorizedNotes
      );
    }
  }

  private handleCategorizedNotes(
    notes: TimeSignal[],
    timeSignalGroups: Map<string, TimeSignal[]>,
    date: string
  ) {
    const categorizedNotes = notes.filter(
      (note: TimeSignal) => !this.isUncategorizedNote(note)
    );
    if (categorizedNotes.length === 0) return;

    const notesSortedByStartDate = this.sortNotesByStartDate(categorizedNotes);
    let currentGroupLabels = this.getLabelAndLabelValueArray(
      notesSortedByStartDate[0]
    );
    let currentGroupIndex = 0;
    for (let i = 0; i < notesSortedByStartDate.length; i++) {
      const currentNote = notesSortedByStartDate[i];
      if (
        isDeepEqual(
          currentGroupLabels,
          this.getLabelAndLabelValueArray(currentNote)
        )
      ) {
        this.appendNoteToGroup(
          timeSignalGroups,
          `labelGroup-${currentGroupIndex.toString(10)}-${date}`,
          currentNote
        );
      } else {
        // If there are multiple notes with identical start times, look ahead for the one matching the prior group's labels.
        const indexOfMatch = this.performLookahead(
          notesSortedByStartDate.slice(i + 1),
          currentGroupLabels,
          currentNote.startTime
        );
        if (indexOfMatch >= 0) {
          const matchingNote = notesSortedByStartDate[indexOfMatch];
          this.appendNoteToGroup(
            timeSignalGroups,
            `labelGroup-${currentGroupIndex.toString(10)}-${date}`,
            matchingNote
          );
          notesSortedByStartDate[indexOfMatch] = currentNote;
        } else {
          currentGroupIndex++;
          currentGroupLabels = this.getLabelAndLabelValueArray(
            notesSortedByStartDate[i]
          );
          this.appendNoteToGroup(
            timeSignalGroups,
            `labelGroup-${currentGroupIndex.toString(10)}-${date}`,
            currentNote
          );
        }
      }
    }
  }

  private isUncategorizedNote(note: TimeSignal) {
    const groupingLabels = this.getGroupingLabels(note);
    return (
      !note.startTime ||
      !note.endTime ||
      groupingLabels.some((labelValue: LabelValueDetails) => {
        return (
          // We used to store the suggested values in the labelValue.value property.
          // So the first clause of the OR conditional is to handle this legacy data.
          (labelValue.valueSource === LabelValueSource.Suggestion &&
            !labelValue.acceptedBy) ||
          (!labelValue?.value?.idValue &&
            !labelValue?.value?.numericValue &&
            !labelValue?.value?.stringValue)
        );
      })
    );
  }

  private appendNoteToGroup(
    map: Map<string, TimeSignal[]>,
    key: string,
    note: TimeSignal
  ): void {
    const group = map.get(key);
    if (!group) {
      map.set(key, [note]);
    } else {
      group.push(note);
    }
  }

  private performLookahead(
    notesToSearch: TimeSignal[],
    labelValues: Partial<LabelValueDetails>[],
    startTime: string
  ): number {
    let i = 0;
    let note = notesToSearch[i];
    while (note?.startTime === startTime) {
      if (note.labelValues === labelValues) {
        return i;
      }

      i++;
      note = notesToSearch[i];
    }

    return -1;
  }

  private sortNotesByStartDate(notes: TimeSignal[]): TimeSignal[] {
    return notes.sort((a: TimeSignal, b: TimeSignal) => {
      if (a.startTime < b.startTime) {
        return -1;
      }

      if (a.startTime > b.startTime) {
        return 1;
      }

      return 0;
    });
  }

  private getLabelAndLabelValueArray(note: TimeSignal) {
    const groupingLabels = this.getGroupingLabels(note);

    return groupingLabels.map((labelValue: LabelValueDetails) => ({
      label: {
        ...labelValue.label
      },
      value: {
        ...labelValue.value
      }
    }));
  }
}
